/**
 * Voice Context Provider - Phase 11.2 Latency Optimization
 * 
 * Provides pre-loaded user context and voice provider configuration
 * to all voice-related components.
 */

import React, { createContext, useContext, ReactNode, useEffect, useState } from 'react';
import { useVoiceContextPreloader, VoiceContext } from '@/hooks/useVoiceContextPreloader';
import { getVoiceConfig, VoiceProviderConfig } from '@/lib/voice/voiceProviderRouter';

interface VoiceContextValue {
  context: VoiceContext;
  isLoading: boolean;
  isStale: boolean;
  refresh: () => void;
  providerConfig: VoiceProviderConfig | null;
}

const VoiceContextContext = createContext<VoiceContextValue | null>(null);

const DEFAULT_PROVIDER_CONFIG: VoiceProviderConfig = {
  preferredTTSProvider: 'auto',
  preferredSTTProvider: 'auto',
  enableFallback: true,
  elevenLabsEnabled: false,
};

export function VoiceContextProvider({ children }: { children: ReactNode }) {
  const preloader = useVoiceContextPreloader();
  const [providerConfig, setProviderConfig] = useState<VoiceProviderConfig | null>(null);

  useEffect(() => {
    getVoiceConfig()
      .then(setProviderConfig)
      .catch(() => setProviderConfig(DEFAULT_PROVIDER_CONFIG));
  }, []);

  return (
    <VoiceContextContext.Provider value={{ ...preloader, providerConfig }}>
      {children}
    </VoiceContextContext.Provider>
  );
}

export function useVoiceContext(): VoiceContextValue {
  const context = useContext(VoiceContextContext);
  
  if (!context) {
    throw new Error('useVoiceContext must be used within a VoiceContextProvider');
  }
  
  return context;
}

// Optional hook that returns null if not in provider (for components that may be outside)
export function useOptionalVoiceContext(): VoiceContextValue | null {
  return useContext(VoiceContextContext);
}
